import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  lighten,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Eye as EyeIcon,
  PaperPlaneTilt,
  PaperPlaneTilt as PaperPlaneTiltIcon,
  UserPlus as UserPlusIcon,
  UsersThree as UsersThreeIcon,
} from '@phosphor-icons/react';
import {
  MaterialReactTable,
  MRT_FilterCheckbox,
  MRT_FilterTextField,
  MRT_GlobalFilterTextField,
  MRT_RowData,
  MRT_Table,
  MRT_TableContainer,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { UserTableTile } from '@/components/core/v2/user-table-tile';
import { InviteAdministratorModal } from './invite-administrator-modal';
import axios from '@/lib/v2/axios';
import { toast } from 'sonner';
import { useConfirm } from 'material-ui-confirm';

interface AdministratorsTableProps {
  rows: User[];
  loading: boolean;
  onCreated: (user: User) => void;
}

export function AdministratorsTable({ rows, loading, onCreated }: AdministratorsTableProps): React.JSX.Element {
  const dialog = useDialog();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 150,
        accessorFn: (row) => {
          return <UserTableTile user={row} />;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 150,
      },
    ],
    []
  );

  const handleSuccess = (user: User) => {
    onCreated(user);
    dialog.handleClose();
  };

  const handleResendInvitation = async (id: string) => {
    try {
      await confirm({
        title: 'Resend Invitation',
        description: 'Are you sure you want to resend the invitation?',
      });
      try {
        const response = await axios.post(`/api/v2/invitations/resend`, {
          user_id: id,
        });

      if (response.status === 200) {
        toast.success('Invitation resent successfully');
      } else {
          toast.error('Failed to resend invitation');
        }
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const table = useMaterialReactTable<User>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <>
        {row.original.email_verified_at !== null ? (
          <IconButton
            onClick={() => {
              navigate(paths.settings.account.administrators.administrator(row.original.id));
            }}
            size="small"
          >
            <EyeIcon size={20} />
          </IconButton>
        ) : (
          <Button color="info" onClick={() => handleResendInvitation(row.original.id)} size="small"
            variant="text"
          >
            Resend Invitation
          </Button>
        )}
      </>
    ),

    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* import MRT sub-components */}
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
          </Box>
        </Box>
      );
    },
  });

  return (
    <React.Fragment>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UsersThreeIcon size={24} style={{ marginRight: '8px', color: 'var(--mui-palette-primary-main)' }} />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle1">
                {rows.length} {rows.length === 1 ? 'Administrator' : 'Administrators'}
              </Typography>
            </Box>
          </Stack>
          
          <Button
            onClick={dialog.handleOpen}
            size="small"
            startIcon={<PaperPlaneTiltIcon size={18} />}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            variant="outlined"
          >
            Invite Administrator
          </Button>
        </Box>

        <MaterialReactTable table={table} />
      </Paper>
      <InviteAdministratorModal onClose={dialog.handleClose} onSuccess={handleSuccess} open={dialog.open} />
    </React.Fragment>
  );
}
